// client/src/pages/schedule/ScheduleForm.js
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext.js';
import { eventTypeMap, sendMailMap } from '../../utils/commonUtils.js';

import CommonDatePicker from "../../components/DatePicker/CommonDatePicker.js";
import "react-datepicker/dist/react-datepicker.css";

import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../components/editor/EditorToolbar.js';
import tiptapExtensions from '../../components/editor/tiptapExtensions.js';
import useEditorWithDragDrop from '../../components/editor/useEditorWithDragDrop.js';
import FileAttachment from '../../components/FileAttachment/FileAttachment.js';
import { createSchedule, updateSchedule, uploadScheduleFiles, expandParticipantsForMeeting, getScheduleExtras } from '../../api/scheduleApi.js';
import { getCurrentUser, uploadEditorImage } from '../../api/common.js';

import axios from 'axios';

import ModalSchedule from './ModalSchedule.js';
import './ModalSchedule.css';

import OrgTreeSelect from '../../components/OrgSelect/OrgTreeSelect.js';

function ScheduleForm({ actionType = 'create', schedule, initialData, onClose, onRefresh, isModal = false }) {

  const isEdit = actionType === 'edit';
  const now = new Date();

  // 일정 기본 정보
  const [title, setTitle] = useState(isEdit ? schedule?.title : '');

  const [startDate, setStartDate] = useState(
    isEdit
      ? new Date(`${schedule?.start_date}T${schedule?.start_time}`)
      : initialData?.start
        ? new Date(initialData.start)
        : now
  );

  const [endDate, setEndDate] = useState(
    isEdit
      ? new Date(`${schedule?.end_date}T${schedule?.end_time}`)
      : initialData?.end
        ? new Date(initialData.end)
        : new Date(now.getTime() + 60 * 60 * 1000)
  );

  // 일정 유형: 기본은 업무일정
  const [scheduleType, setScheduleType] = useState(isEdit ? schedule?.event_type : 'W');
  // 메일 발송 여부: 작성 기본값은 "N"(미발송)
  const [sendMailYn, setSendMailYn] = useState(
    isEdit ? (schedule?.sendMailYn ?? "N") : "N"
  );

  // 파일/참석자
  const [files, setFiles] = useState([]);
  const [participants, setParticipants] = useState(isEdit ? schedule?.participants || [] : []);
  const [isParticipantsExpanded, setIsParticipantsExpanded] = useState(false);

  const [orgTargetType, setOrgTargetType] = useState('all'); // 조직도 타겟 타입 상태
  const [orgSelectionMode, setOrgSelectionMode] = useState('multiple'); // 조직도 선택 모드 (single/multiple)

  // 검색 관련
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [showPopup, setShowPopup] = useState(false);

  // ✅ 조직/부서 관련 상태 (빠졌던 부분 복원)
  const [showOrgPopup, setShowOrgPopup] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [topDept, setTopDept] = useState(null);
  const [selectedDept, setSelectedDept] = useState(null);
  const [type, setType] = useState(null);

  const [selectedTargets, setSelectedTargets] = useState([]);

  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const [userInfo, setUserInfo] = useState(null); // 사용자 정보 상태

  const uploadImage = (file) => uploadEditorImage(file, token, 'schedule');
  const editor = useEditorWithDragDrop(tiptapExtensions, '', { onImageUpload: uploadImage });

  const toDateText = (d) => {
    if (!d) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const toTimeText = (d) => {
    if (!d) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}:00`;
  };

  // 일정 수정 모드 초기화

  useEffect(() => {
    if (token) {
      getCurrentUser(token)
        .then((info) => {
          setUserInfo(info); // 다른 곳에서 쓰기 위해 상태에 저장
        })
        .catch((err) => console.error('사용자 정보 로드 실패:', err));
    }
  }, [token]);

  useEffect(() => {
    if (departments && departments.length > 0) {
      setTopDept(departments[0]);
    }
  }, [departments]);

  // ✅ 수정 모드 초기화
  useEffect(() => {
    if (isEdit && schedule && editor) {
      // 기본 정보 복원
      setTitle(schedule.title);
      setScheduleType(schedule.event_type);
      editor.commands.setContent(schedule.content || '');

      // ✅ extras 다시 가져오기 (첨부/참석자)
      getScheduleExtras(schedule.id, token)
        .then(res => {
          // schedule 객체에 덮어쓰기
          schedule.files = res.files || [];
          schedule.targets = res.targets || [];

          // 첨부파일 복원
          const cleanedFiles = (schedule.files || []).filter(f => f && f.id && f.originalName);
          const uniqueFiles = Array.from(new Map(cleanedFiles.map(f => [f.id, f])).values())
            .map(f => ({
              id: f.id,
              name: f.originalName,
              size: f.size,
              mimeType: f.mimeType || f.mimetype || f.type || "unknown",
              path: f.path,
              originalName: f.originalName,
              isNew: false,
              isDeleted: false,
            }));
          setFiles(uniqueFiles);

          // 참석자 복원
          const uniqueTargets = Array.from(
            new Map((schedule.targets || []).map(t => [t.target_id + t.target_type, t])).values()
          ).map(t => ({
            target_id: t.target_id,
            target_type: t.target_type,
            target_name: t.target_name || t.dept_name || "",
            disp_target_name: t.disp_target_name,
            checked: false,
          }));
          setParticipants(uniqueTargets);
          setSelectedTargets(uniqueTargets);

          // ✅ 업무일정(W)일 때 부서 계층 복원
          if (schedule.event_type === "W" && schedule.dept_code) {
            fetch(`/api/dept/${schedule.dept_code}`, {
              headers: { Authorization: `Bearer ${token}` }
            })
              .then(res => res.json())
              .then(dept => {
                if (dept && dept.full_dept_name && dept.full_dept_code) {
                  const names = dept.full_dept_name.split(" > ");
                  const codes = dept.full_dept_code.split(" > ");
                  const expanded = names.map((name, idx) => ({
                    dept_name: name,
                    dept_code: codes[idx] ?? "",
                    full_dept_name: names.slice(0, idx + 1).join(" > "),
                    full_dept_code: codes.slice(0, idx + 1).join(" > ")
                  }));

                  setDepartments(expanded);
                  setTopDept(expanded[0] || null);

                  // 기본 선택값 복원
                  const targetDept = uniqueTargets.find(t => t.target_type === "dept");
                  if (targetDept) {
                    const selected = expanded.find(d => d.dept_code === targetDept.target_id);
                    if (selected) {
                      setSelectedDept(selected);
                    }
                  }
                }
              })
              .catch(err => {
                console.error("▶ dept 조회 에러:", err);
                setDepartments([]);
              });
          }
        })
        .catch(err => console.error("▶ edit 모드 extras 조회 오류:", err));
    }
  }, [isEdit, schedule, editor, token]);

  // 신규 작성 모드
  useEffect(() => {
    if (isEdit) return;
    if (!user?.userid) return;

    fetch(`/api/auth/user/${user.userid}/departments`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        const deptArray = Array.isArray(data) ? data : [data];

        const expanded = deptArray
          .filter(d => d && d.full_dept_name && d.full_dept_code)
          .flatMap(dept => {
            const names = dept.full_dept_name.split(" > ");
            const codes = dept.full_dept_code.split(" > ");
            return names.map((name, idx) => ({
              dept_name: name,
              dept_code: codes[idx] ?? "",
              full_dept_name: names.slice(0, idx + 1).join(" > "),
              full_dept_code: codes.slice(0, idx + 1).join(" > ")
            }));
          });

        const unique = expanded.filter(
          (item, idx, arr) =>
            arr.findIndex(d => d.dept_code === item.dept_code) === idx
        );

        setDepartments(unique);
        setTopDept(expanded[0] || null);   // ✅ 최상위 부서 저장
        // 신규 작성 시에는 기본 선택값 없음 → "부서를 선택하세요"
        setSelectedDept(null);
      })
      .catch(err => {
        console.error("▶ fetch 에러 발생:", err);
        setDepartments([]);
      });
  }, [isEdit, user, token]);

  // ✅ 참석자 체크/삭제 함수
  const toggleCheck = (id) => {
    setParticipants(prev =>
      prev.map(p =>
        p.target_id === id ? { ...p, checked: !p.checked } : p
      )
    );
  };

  const deleteSelected = () => {
    setParticipants(prev => {
      const filtered = prev.filter(p => !p.checked);
      return filtered;
    });
  };

  const deleteAll = () => {
    setParticipants([]);
    setSelectedTargets([]);
  };

  const count = participants.length;

  // ✅ 저장 함수 (최종본)
  const handleSubmit = async (e) => {
    e.preventDefault();
    // 날짜/시간 검증
    if (!startDate || !endDate) {
      alert("시작/종료 일시를 모두 선택하세요.");
      return;
    }
    if (endDate <= startDate) {
      alert("종료일시는 시작일시보다 늦어야 합니다.");
      return;
    }
    const now = new Date();
    if (startDate <= now) {
      alert("시작일시는 현재 시각보다 늦어야 합니다.");
      return;
    }

    // 참석자 검증
    if (scheduleType === "W") {
      const hasDept = selectedTargets.some(t => t.target_type === "dept");
      if (!hasDept) {
        alert("업무일정은 반드시 부서를 선택해야 합니다.");
        return;
      }
    } else if (scheduleType === "S") {
      const hasUserOrDept = selectedTargets.some(
        t => t.target_type === "user" || t.target_type === "dept"
      );
      if (!hasUserOrDept) {
        alert("공유일정은 반드시 참석자를 등록해야 합니다.");
        return;
      }
    } else if (scheduleType === "M") {
      const hasUserOrDept = selectedTargets.some(
        t => t.target_type === "user" || t.target_type === "dept"
      );
      if (!hasUserOrDept) {
        alert("회의는 반드시 참석자를 등록해야 합니다.");
        return;
      }
    }

    // 삭제된 파일 ID 추출
    const deletedFileIds = files
      .filter(f => f.isDeleted && f.id != null)
      .map(f => f.id);

    // 일정구분별 participantsData 처리
    let participantsData = selectedTargets;
    if (scheduleType === "M") {
      participantsData = await expandParticipantsForMeeting(selectedTargets, token);
    }
    const currentParticipants = participantsData;
    const hasTopLevelTarget = (
      scheduleType === "W" &&
      currentParticipants.some(p => {
        return p.target_id?.toLowerCase() === topDept.dept_code; // 대소문자 무시
      })
    );

    // 일정 데이터 구성 (JSON)
    const scheduleData = {
      title,
      writer_id: user?.userid,
      writer_name: user?.name,
      writer_post: userInfo?.position || null,
      dept_name: userInfo?.dept_name || null,
      dept_code: user?.dept_code || null,
      start_date: toDateText(startDate),
      start_time: toTimeText(startDate),
      end_date: toDateText(endDate),
      end_time: toTimeText(endDate),
      event_place: "",
      event_type: scheduleType,
      participants: currentParticipants.map(t => ({
        target_from: scheduleType,
        target_type: t.target_type,
        target_id: t.target_id,
        target_name: t.target_name || t.dept_name || "",
        disp_target_name: t.disp_target_name
      })),
      content: editor.getHTML(),
      //status: isEdit ? schedule?.status : "register",
      status: isEdit ? schedule?.status : (hasTopLevelTarget ? "request" : "register"),
      deletedFileIds,
    };
    try {
      if (isEdit) {
        // 수정 모드 → JSON 업데이트
        await updateSchedule(scheduleData, schedule.id, token);
        // 신규 파일 업로드
        const newFiles = files.filter(f => f.isNew);
        if (newFiles.length > 0) {
          const formData = new FormData();
          newFiles.forEach(f => formData.append("files", f.file));
          await uploadScheduleFiles(schedule.id, formData, token);
        }
      } else {
        // 등록 모드 → JSON 등록
        const newSchedule = await createSchedule(scheduleData, token);
        if (newSchedule?.id) {
          const newFiles = files.filter(f => f.isNew);
          if (newFiles.length > 0) {
            const formData = new FormData();
            newFiles.forEach(f => formData.append("files", f.file));
            await uploadScheduleFiles(newSchedule.id, formData, token);
          }
        }
      }
      //alert(isEdit ? "일정이 수정되었습니다." : "일정이 등록되었습니다.");
      if (isEdit) {
        alert("일정이 수정되었습니다.");
        if (onRefresh) onRefresh();
        if (onClose) onClose();
      } else {
        if (scheduleType === "W" && hasTopLevelTarget) {
          alert("업무일정(최상위부서)이 등록요청 되었습니다.");
        } else {
          alert("일정이 등록되었습니다.");
        }
        if (onRefresh) onRefresh();
        navigate("/schedule/calendar");
      }
    } catch (err) {
      console.error("일정 저장 실패:", err);
      alert(isEdit ? "일정 수정 중 오류가 발생했습니다." : "일정 등록 중 오류가 발생했습니다.");
    }
  };

  // 🔍 검색 함수
  const handleSearch = (term) => {
    const keyword = (term || '').trim();
    setSearchTerm(keyword);

    if (keyword === '') {
      alert("검색대상이 입력되지 않았습니다.");
      return;
    }

    const token = sessionStorage.getItem('token');

    axios.get('/api/org/search', {
      params: { keyword },
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        const { users = [], depts = [] } = res.data;

        if (users.length === 0 && depts.length === 0) {
          alert("검색 결과가 존재하지 않습니다.");
          return;
        }

        // ✅ 단일 사용자 결과 → 바로 참석자 추가
        if (users.length === 1 && depts.length === 0) {
          const u = users[0];
          const target = {
            target_from: scheduleType,
            target_type: 'user',
            target_id: u.userid || u.id,
            target_name: u.name,
            disp_target_name: `${u.name}${u.position ? " / " + u.position : ""}${u.dept_name ? " (" + u.dept_name + ")" : ""}`,
          };
          setSelectedTargets(prev => [...prev, target]);   // ✅ 누적 추가
          handleSelect(target);
          return;
        }

        // ✅ 단일 부서 결과 → 바로 참석자 추가
        if (depts.length === 1 && users.length === 0) {
          const d = depts[0];
          const target = {
            target_from: scheduleType,
            target_type: 'dept',
            target_id: d.dept_code,
            target_name: d.dept_name,
            disp_target_name: d.full_dept_name,
          };
          setSelectedTargets(prev => [...prev, target]);   // ✅ 누적 추가
          handleSelect(target);
          return;
        }

        // ✅ 다수 결과 → 검색 결과 모달 띄우기
        setUsers(users);
        setDepts(depts);
        setType('searchResult');
        setShowPopup(true);
      })
      .catch(err => console.error('검색 실패:', err));
  };
  // 선택된 사용자/부서 추가/삭제 처리
  const handleSelect = (target) => {
    setParticipants(prev => {
      // 이미 추가된 참석자 중복 방지
      const exists = prev.some(
        p => p.target_id === target.target_id && p.target_type === target.target_type
      );
      if (exists) return prev;

      const participant = {
        target_from: scheduleType,          // ✅ 일정 타입 정보
        target_type: target.target_type,    // 'user' 또는 'dept'
        target_id: target.target_id,        // 사용자 ID 또는 부서 코드
        target_name: target.target_name,    // 원래 이름/부서명
        disp_target_name: target.disp_target_name, // 화면 표시용 문자열
        checked: false,                     // 체크박스 상태
      };

      return [...prev, participant];
    });

    // ✅ selectedTargets도 항상 participants와 동일하게 유지

    setSelectedTargets(prev => {
      const exists = prev.some(
        t => t.target_id === target.target_id && t.target_type === target.target_type
      );
      if (exists) return prev;
      return [...prev, target];
    });
  };

  // ✅ 선택 토글 함수
  const toggleSelect = (item, type) => {
    if (type === 'user') {
      setUsers(prev =>
        prev.map(u =>
          u.userid === item.userid ? { ...u, checked: !u.checked } : u
        )
      );
    } else if (type === 'dept') {
      setDepts(prev =>
        prev.map(d =>
          d.dept_code === item.dept_code ? { ...d, checked: !d.checked } : d
        )
      );
    }
  };

  // 📌 확인 버튼 함수
  const handleConfirm = () => {
    const selectedUsers = users.filter(u => u.checked);
    const selectedDepts = depts.filter(d => d.checked);
    selectedUsers.forEach(u => addParticipant(u));
    selectedDepts.forEach(d => addParticipant(d));
    setShowPopup(false);
  };

  const normalizeTargets = (targets) => {
    return targets.map(node => {
      if (node.target_type === 'user') {
        // 사용자
        return {
          target_from: scheduleType, // ✅ 일정 타입 정보
          target_type: 'user',
          target_id: String(node.target_id),
          target_name: node.target_name || '', // 원래 이름
          disp_target_name: node.disp_target_name,
          checked: false,
        };
      } else {
        // 부서
        return {
          target_from: scheduleType,
          target_type: 'dept',
          target_id: String(node.target_id),
          target_name: node.dept_name || node.target_name || '', // 원래 부서명
          disp_target_name: node.disp_target_name,
          checked: false,
        };
      }
    });
  };

  const handleConfirmOrgSelection = (targets) => {
    // ✅ normalizeTargets: 사용자/부서 데이터를 통일된 구조로 변환
    debugger;
    const normalizedTargets = normalizeTargets(targets);

    // ✅ 새로 반영할 참석자 배열 생성
    const newParticipants = normalizedTargets.map(t => ({
      target_from: scheduleType,            // 일정 타입 정보
      target_type: t.target_type,           // 'user' 또는 'dept'
      target_id: String(t.target_id),       // 사용자 ID 또는 부서 코드
      target_name: t.target_name,           // 원래 이름/부서명
      disp_target_name: t.disp_target_name, // 화면 표시용 문자열
      // checked 상태는 모달에서 관리 → 여기서 강제 초기화하지 않음
    }));
    // ✅ 기존 값 초기화 후 새로 세팅
    setSelectedTargets([]);
    setParticipants([]);

    setSelectedTargets(newParticipants);
    setParticipants(newParticipants);
  };

  // 참석자 추가 함수
  const addParticipant = (item, searchKeyword) => {
    setParticipants(prev => {
      const exists = prev.some(
        p => p.target_id === (item.userid || item.dept_code) &&
          p.target_type === (item.userid ? 'user' : 'dept')
      );
      if (exists) return prev;

      let participant;
      if (item.userid) {
        // 사용자
        participant = {
          target_from: scheduleType, // 일정 타입 정보
          target_type: 'user',
          target_id: item.userid,
          target_name: item.name, // 원래 이름
          disp_target_name: `${item.name}${item.position ? " / " + item.position : ""}${item.dept_name ? " (" + item.dept_name + ")" : ""}`, // 표시용 문자열
          checked: false,
        };
      } else if (item.dept_code) {
        // 부서
        participant = {
          target_from: scheduleType,
          target_type: 'dept',
          target_id: item.dept_code,
          target_name: item.dept_name, // 원래 부서명
          disp_target_name: item.full_dept_name, // 전체 경로
          checked: false,
        };
      }

      return participant ? [...prev, participant] : prev;
    });

    // ✅ selectedTargets도 항상 participants와 동일하게 유지
    setSelectedTargets(prev => {
      const exists = prev.some(
        t => t.target_id === (item.userid || item.dept_code) &&
          t.target_type === (item.userid ? 'user' : 'dept')
      );
      if (exists) return prev;

      const target = item.userid
        ? {
          target_from: scheduleType,
          target_type: 'user',
          target_id: item.userid,
          target_name: item.name,
          disp_target_name: `${item.name}${item.position ? " / " + item.position : ""}${item.dept_name ? " (" + item.dept_name + ")" : ""}`,

        }
        : {
          target_from: scheduleType,
          target_type: 'dept',
          target_id: item.dept_code,
          target_name: item.dept_name,
          disp_target_name: item.full_dept_name,
        };

      return [...prev, target];
    });

  };

  const scheduleContent = (
    <form onSubmit={handleSubmit} className={isModal ? "form-schedule-flex-form" : ""}>
      <div className={isModal ? "form-schedule-scroll-area" : ""}>
        {/* 기본 정보 카드 */}
        <div className="card mb-3 form-schedule-card">
          <div className="card-header fw-bold">기본 정보</div>
          <div className="card-body">
            <div className="form-schedule-title mb-3">
              <label>제목</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault(); // ✅ 엔터 시 submit 막기
                  }
                }}
                className="form-control"
                required
              />
            </div>

            <div className="form-schedule-type mb-3">
              <label>일정구분</label>
              <div className="form-schedule-radio-group">
                {Object.entries(eventTypeMap).map(([code, label]) => (
                  <label key={code} className="form-check me-3">
                    <input
                      type="radio"
                      value={code}
                      checked={scheduleType === code}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setScheduleType(newType);
                        // 일정구분이 변경되면 참석자 및 부서 선택 초기화
                        setParticipants([]);
                        setSelectedTargets([]);
                        setSelectedDept(null);
                        setSearchTerm('');
                        setUsers([]);
                        setDepts([]);
                      }}
                      className="form-check-input"
                      disabled={isEdit}   // ✅ 수정 모드에서는 선택 불가
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-schedule-dates mb-3">
              <label>일정일시</label>
              <div className="d-flex align-items-center">
                <CommonDatePicker
                  value={startDate}
                  onChange={setStartDate}
                  showTime={true}
                  calendarClassName={isModal ? "start-datepicker-open" : undefined}
                />
                <span className="mx-2">~</span>
                <CommonDatePicker value={endDate} onChange={setEndDate} showTime={true} minDate={startDate} />
              </div>
            </div>

          </div>
        </div>

        {/* 참석자 카드 */}
        {(scheduleType === "W" || scheduleType === "S" || scheduleType === "M") && (
          <div className="card mb-3 form-schedule-card">
            <div className="card-header fw-bold">참석자 관리</div>
            <div className="card-body pb-3">

              {/* 부서 선택 (W일 때만) */}
              {scheduleType === "W" && (
                <div className="form-schedule-department mb-3">
                  <label>부서 선택</label>
                  <select
                    value={selectedDept ? selectedDept.dept_code : ""}
                    className="form-select"
                    onChange={(e) => {
                      const deptCode = e.target.value;
                      const selected = departments.find(d => d.dept_code === deptCode);
                      setSelectedDept(selected);

                      if (selected) {
                        const newTarget = {
                          target_id: selected.dept_code,
                          target_name: selected.dept_name,
                          target_type: 'dept',
                          disp_target_name: selected.full_dept_name,
                        };
                        setSelectedTargets([newTarget]);

                        // ✅ 최상위 부서 선택 시 alert 띄우기
                        if (selected.dept_code === departments[0]?.dept_code) {
                          alert("관리자 승인 후 등록됩니다.\n등록요청 버튼을 클릭하시기 바랍니다.");
                        }
                      } else {
                        setSelectedTargets([]);
                      }
                    }}
                  >
                    <option value="">부서를 선택하세요</option>
                    {Array.isArray(departments) &&
                      departments.map((dept) => (
                        <option key={dept.dept_code} value={dept.dept_code}>
                          {dept.full_dept_name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {/* 참석자 관리 (S, M일 때) */}
              {(scheduleType === "S" || scheduleType === "M") && (
                <div className="form-schedule-participants mb-0">
                  <div className="form-schedule-search-bar mb-2 d-flex justify-content-between">
                    <div className="form-schedule-search-left">
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const keyword = (searchTerm || '').trim();
                            if (keyword !== '') {
                              handleSearch(keyword);
                              setSearchTerm('');
                            }
                          }
                        }}
                        placeholder="검색"
                        className="form-control d-inline-block w-auto me-2"
                      />
                      <button
                        type="button"
                        className="btn btn-sm me-2"
                        style={{ backgroundColor: '#343a40', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px' }}
                        onClick={() => handleSearch(searchTerm)}
                      >
                        검색
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ backgroundColor: 'transparent', border: '1px solid #007bff', color: '#007bff', padding: '4px 12px', borderRadius: '4px' }}
                        onClick={() => {
                          setOrgSelectionMode('multiple');
                          setOrgTargetType('all');
                          setType('orgTreeSelect');     // 모달 종류를 조직도로 지정
                          setShowOrgPopup(true);        // 조직도 팝업 열기
                        }}
                      >
                        조직도 선택
                      </button>
                    </div>

                    {scheduleType === "M" && (
                      <div className="form-schedule-mail-option">
                        <label className="me-3">
                          <input
                            type="radio"
                            name="mailOption"
                            value="Y"
                            checked={sendMailYn === "Y"}
                            onChange={(e) => setSendMailYn(e.target.value)}
                          />
                          {sendMailMap["Y"]}
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="mailOption"
                            value="N"
                            checked={sendMailYn === "N"}
                            onChange={(e) => setSendMailYn(e.target.value)}
                          />
                          {sendMailMap["N"]}
                        </label>
                      </div>
                    )}
                  </div>

                  {/* 참석자 리스트 */}
                  <div className="form-schedule-content d-flex mb-0">
                    <div
                      className="form-schedule-list-box"
                      style={{
                        flex: '0 0 80%', // ✅ 다시 넓이 축소
                        height: isParticipantsExpanded ? 'auto' : '115px',
                        overflowY: isParticipantsExpanded ? 'visible' : 'auto',
                        minHeight: 'auto'
                      }}
                    >
                      {participants.length === 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6c757d', fontSize: '0.9rem' }}>
                          참석자가 없습니다.
                        </div>
                      ) : (
                        <>
                          {participants.map((p) => (
                            <label key={p.target_id} className="d-block">
                              <input
                                type="checkbox"
                                checked={p.checked || false}
                                onChange={() => toggleCheck(p.target_id)}
                                className="form-check-input me-2"
                              />
                              {p.disp_target_name}   {/* ✅ 무조건 disp_target_name만 출력 */}
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                    <div className="form-schedule-delete-buttons d-flex flex-column align-items-start gap-2" style={{ flex: '1' }}>
                      {/* 상단: 확장버튼 + 카운트 (왼쪽으로 바짝 붙임) */}
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="d-flex align-items-center">
                          {participants.length > 4 && (
                            <button
                              type="button"
                              className="btn-height"
                              onClick={() => setIsParticipantsExpanded(!isParticipantsExpanded)}
                            >
                              <i className={`fas ${isParticipantsExpanded ? 'fa-minus' : 'fa-plus'}`}></i>
                            </button>
                          )}
                        </div>
                        <div className="form-schedule-count fw-bold" style={{ whiteSpace: 'nowrap' }}>Count : {count}</div>
                      </div>

                      {/* 하단: 선택삭제/전체삭제 버튼 가로 배치 (크기 복원 및 왼쪽 정렬) */}
                      <div className="d-flex align-items-center gap-1">
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={deleteSelected}>선택삭제</button>
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={deleteAll}>전체삭제</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* 내용 카드 */}
        <div className="card mb-3 form-schedule-card">
          <div className="card-header fw-bold">내용</div>
          <div className="card-body">
            {editor && <EditorToolbar editor={editor} onImageUpload={uploadImage} />}
            <EditorContent editor={editor} className="editor-content" />
          </div>
        </div>

        {/* 첨부파일 카드 */}
        <div className="card mb-3 form-schedule-card">
          <div className="card-header fw-bold">첨부파일</div>
          <div className="card-body">
            <FileAttachment files={files} setFiles={setFiles} mode="edit" />
          </div>
        </div>
      </div>

      <div className={isModal ? "form-schedule-fixed-buttons" : "card form-schedule-card"}>
        <div className={isModal ? "text-end" : "card-body text-end"}>
          <button type="submit" className="btn btn-success">
            {selectedDept && topDept && selectedDept.dept_code === topDept.dept_code
              ? "등록요청"
              : isEdit
                ? "일정 수정"
                : "일정 등록"}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary ms-2"
            onClick={() => {
              if (isModal) {
                onClose(); // 부모에서 내려준 setIsFormOpen(false) 실행
              } else {
                navigate("/schedule/calendar");
              }
            }}
          >
            닫기
          </button>
        </div>
      </div>

    </form>
  )

  return (
    <div className="form-schedule-container">
      {/* 전체 타이틀 */}
      <div className="form-schedule-title-header">
        {isEdit ? "일정 수정" : "일정 등록"}
      </div>

      {isModal ? (
        <div className="form-schedule-modal_form">
          {scheduleContent}
        </div>
      ) : (
        scheduleContent
      )}

      {/* 팝업들 */}
      {type === "searchResult" && showPopup && (
        <ModalSchedule
          type="searchResult"
          data={{ users, depts }}
          toggleSelect={toggleSelect}
          onConfirm={handleConfirm}
          onClose={() => setShowPopup(false)}
        />
      )}

      {type === "orgTreeSelect" && showOrgPopup && (
        <OrgTreeSelect
          onConfirm={handleConfirmOrgSelection}
          onCancel={() => setShowOrgPopup(false)}
          selectionMode={orgSelectionMode}
          targetType={orgTargetType}
          initialSelected={participants}
        />
      )}
    </div>
  );
}

export default ScheduleForm;